import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import {
  partnerProductsService,
  partnerServicesService,
  productsCatalogService,
  servicesCatalogService,
} from '../../../services/catalogService';

// ── Searchable Dropdown ───────────────────────────────────────────────────────
const SearchableDropdown = ({ options, value, onChange, placeholder, labelKey, subKey }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const selected = options?.find(o => o?.id === value);
  const filtered = options?.filter(o =>
    o?.[labelKey]?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (subKey && o?.[subKey]?.toLowerCase()?.includes(search?.toLowerCase()))
  );

  useEffect(() => {
    const handler = (e) => { if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false); };
    document?.addEventListener('mousedown', handler);
    return () => document?.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 bg-white flex items-center justify-between gap-8 text-left"
      >
        <span className={selected ? 'text-slate-900' : 'text-slate-400'}>
          {selected ? selected?.[labelKey] : placeholder}
        </span>
        <Icon name="ChevronDown" size={16} className={`text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-4 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-8 border-b border-slate-100">
            <div className="flex items-center gap-8 px-10 py-8 bg-slate-50 rounded-lg">
              <Icon name="Search" size={14} className="text-slate-400 flex-shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent text-sm outline-none text-slate-700 placeholder-slate-400"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered?.length === 0 ? (
              <div className="px-14 py-12 text-sm text-slate-400 text-center">No results found</div>
            ) : (
              filtered?.map(opt => (
                <button
                  key={opt?.id}
                  type="button"
                  onClick={() => { onChange(opt?.id); setOpen(false); setSearch(''); }}
                  className={`w-full px-14 py-10 text-left hover:bg-primary/5 transition-colors ${value === opt?.id ? 'bg-primary/10' : ''}`}
                >
                  <div className="text-sm font-medium text-slate-900">{opt?.[labelKey]}</div>
                  {subKey && opt?.[subKey] && <div className="text-xs text-slate-500 mt-1">{opt?.[subKey]}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Add Product Modal ─────────────────────────────────────────────────────────
const AddProductModal = ({ partnerId, linkedProductIds, onClose, onSuccess }) => {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [form, setForm] = useState({
    catalogProductId: '',
    authorizationLevel: 'Reseller',
    marginPct: '',
    isStrategic: false,
    linkStatus: 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    productsCatalogService?.getActive()?.then(data => {
        const available = data?.filter(p => !linkedProductIds?.includes(p?.id));
        setCatalog(available);
      })?.catch(err => setError(err?.message))?.finally(() => setLoadingCatalog(false));
  }, []);

  const selectedProduct = catalog?.find(p => p?.id === form?.catalogProductId);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.catalogProductId) { setError('Please select a product.'); return; }
    setSaving(true);
    setError(null);
    try {
      await partnerProductsService?.link(partnerId, form?.catalogProductId, {
        authorizationLevel: form?.authorizationLevel,
        marginPct: form?.marginPct ? parseFloat(form?.marginPct) : 0,
        isStrategic: form?.isStrategic,
        linkStatus: form?.linkStatus,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to add product.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Icon name="Package" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Add Product</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-28 space-y-18 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Select Product *</label>
            {loadingCatalog ? (
              <div className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl text-slate-400">Loading catalog...</div>
            ) : (
              <SearchableDropdown
                options={catalog}
                value={form?.catalogProductId}
                onChange={v => setForm(prev => ({ ...prev, catalogProductId: v }))}
                placeholder="Search and select a product..."
                labelKey="productName"
                subKey="oemBrand"
              />
            )}
            {selectedProduct && (
              <div className="mt-10 p-12 bg-blue-50 border border-blue-100 rounded-xl grid grid-cols-2 gap-8 text-xs">
                <div><span className="text-slate-500">Category:</span> <span className="font-medium text-slate-800">{selectedProduct?.category}</span></div>
                <div><span className="text-slate-500">Brand:</span> <span className="font-medium text-slate-800">{selectedProduct?.oemBrand || '—'}</span></div>
                <div><span className="text-slate-500">SKU:</span> <span className="font-medium text-slate-800">{selectedProduct?.modelSku || '—'}</span></div>
                <div><span className="text-slate-500">Price Range:</span> <span className="font-medium text-slate-800">{selectedProduct?.priceRange || '—'}</span></div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Authorization Level</label>
              <select
                value={form?.authorizationLevel}
                onChange={e => setForm(prev => ({ ...prev, authorizationLevel: e?.target?.value }))}
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
              >
                {['Reseller', 'Distributor', 'OEM', 'Standard']?.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Margin %</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form?.marginPct}
                onChange={e => setForm(prev => ({ ...prev, marginPct: e?.target?.value }))}
                placeholder="e.g. 15"
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Status</label>
              <select
                value={form?.linkStatus}
                onChange={e => setForm(prev => ({ ...prev, linkStatus: e?.target?.value }))}
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-10 cursor-pointer">
                <div
                  onClick={() => setForm(prev => ({ ...prev, isStrategic: !prev?.isStrategic }))}
                  className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-colors cursor-pointer ${form?.isStrategic ? 'bg-amber-500 border-amber-500' : 'border-slate-300 bg-white'}`}
                >
                  {form?.isStrategic && <Icon name="Check" size={14} className="text-white" />}
                </div>
                <span className="text-sm font-medium text-slate-700">Mark as Strategic</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving || loadingCatalog} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Adding...</> : <><Icon name="Plus" size={16} /> Add Product</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Add Service Modal ─────────────────────────────────────────────────────────
const AddServiceModal = ({ partnerId, linkedServiceIds, onClose, onSuccess }) => {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [form, setForm] = useState({
    catalogServiceId: '',
    coverageArea: '',
    deliveryCapability: 'Partner-Led',
    slaCommitment: '',
    linkStatus: 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    servicesCatalogService?.getActive()?.then(data => {
        const available = data?.filter(s => !linkedServiceIds?.includes(s?.id));
        setCatalog(available);
      })?.catch(err => setError(err?.message))?.finally(() => setLoadingCatalog(false));
  }, []);

  const selectedService = catalog?.find(s => s?.id === form?.catalogServiceId);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.catalogServiceId) { setError('Please select a service.'); return; }
    setSaving(true);
    setError(null);
    try {
      await partnerServicesService?.link(partnerId, form?.catalogServiceId, {
        coverageArea: form?.coverageArea,
        deliveryCapability: form?.deliveryCapability,
        slaCommitment: form?.slaCommitment,
        linkStatus: form?.linkStatus,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to add service.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-purple-500/5 to-pink-500/5 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Icon name="Wrench" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Add Service</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-28 space-y-18 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Select Service *</label>
            {loadingCatalog ? (
              <div className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl text-slate-400">Loading catalog...</div>
            ) : (
              <SearchableDropdown
                options={catalog}
                value={form?.catalogServiceId}
                onChange={v => setForm(prev => ({ ...prev, catalogServiceId: v }))}
                placeholder="Search and select a service..."
                labelKey="serviceName"
                subKey="serviceCategory"
              />
            )}
            {selectedService && (
              <div className="mt-10 p-12 bg-purple-50 border border-purple-100 rounded-xl grid grid-cols-2 gap-8 text-xs">
                <div><span className="text-slate-500">Category:</span> <span className="font-medium text-slate-800">{selectedService?.serviceCategory}</span></div>
                <div><span className="text-slate-500">Delivery Model:</span> <span className="font-medium text-slate-800">{selectedService?.deliveryModel || '—'}</span></div>
                <div className="col-span-2"><span className="text-slate-500">SLA Levels:</span> <span className="font-medium text-slate-800">{selectedService?.slaLevels || '—'}</span></div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Coverage Area</label>
              <input
                value={form?.coverageArea}
                onChange={e => setForm(prev => ({ ...prev, coverageArea: e?.target?.value }))}
                placeholder="e.g. MENA, UAE"
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Delivery Capability</label>
              <select
                value={form?.deliveryCapability}
                onChange={e => setForm(prev => ({ ...prev, deliveryCapability: e?.target?.value }))}
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
              >
                {['Partner-Led', 'Joint', 'eCity-Led']?.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">SLA Commitment</label>
              <input
                value={form?.slaCommitment}
                onChange={e => setForm(prev => ({ ...prev, slaCommitment: e?.target?.value }))}
                placeholder="e.g. 4-hour response"
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Status</label>
              <select
                value={form?.linkStatus}
                onChange={e => setForm(prev => ({ ...prev, linkStatus: e?.target?.value }))}
                className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving || loadingCatalog} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Adding...</> : <><Icon name="Plus" size={16} /> Add Service</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ProductsServicesSection = ({ partnerId }) => {
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddService, setShowAddService] = useState(false);

  const loadItems = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const [products, services] = await Promise.all([
        partnerProductsService?.getByPartner(partnerId),
        partnerServicesService?.getByPartner(partnerId),
      ]);
      const mapped = [
        ...(products || [])?.map(p => ({
          id: p?.id,
          catalogId: p?.catalogProductId,
          name: p?.productName,
          type: 'product',
          category: p?.category,
          strategic: p?.isStrategic,
          status: p?.linkStatus === 'active' ? 'Active' : 'Inactive',
          description: p?.licensingModel ? `Licensing: ${p?.licensingModel}` : '',
          extra: p?.authorizationLevel ? `Auth: ${p?.authorizationLevel}` : '',
          priceRange: p?.priceRange,
          marginPct: p?.marginPct,
        })),
        ...(services || [])?.map(s => ({
          id: s?.id,
          catalogId: s?.catalogServiceId,
          name: s?.serviceName,
          type: 'service',
          category: s?.serviceCategory,
          strategic: false,
          status: s?.linkStatus === 'active' ? 'Active' : 'Inactive',
          description: s?.deliveryCapability ? `Delivery: ${s?.deliveryCapability}` : '',
          extra: s?.coverageArea ? `Coverage: ${s?.coverageArea}` : '',
          slaCommitment: s?.slaCommitment,
        })),
      ];
      setItems(mapped);
    } catch (err) {
      console.error('Error loading partner products/services:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [partnerId]);

  const linkedProductIds = items?.filter(i => i?.type === 'product')?.map(i => i?.catalogId);
  const linkedServiceIds = items?.filter(i => i?.type === 'service')?.map(i => i?.catalogId);

  const filteredItems = filter === 'all' ? items : items?.filter(item => item?.type === filter);

  const ProductCard = ({ item }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-24 hover:shadow-elevation-md transition-smooth">
      <div className="flex items-start justify-between mb-16">
        <div className="flex items-center gap-12">
          <div className={`p-10 rounded-lg ${item?.type === 'product' ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10' : 'bg-gradient-to-br from-purple-500/10 to-pink-500/10'}`}>
            <Icon name={item?.type === 'product' ? 'Package' : 'Wrench'} size={20} className={item?.type === 'product' ? 'text-blue-600' : 'text-purple-600'} />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">{item?.name}</h4>
            <p className="text-sm text-slate-500">{item?.category}</p>
          </div>
        </div>
        {item?.strategic && (
          <span className="px-12 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold rounded-full flex items-center gap-4">
            <Icon name="Star" size={12} />Strategic
          </span>
        )}
      </div>
      {item?.description && <p className="text-sm text-slate-600 mb-12">{item?.description}</p>}
      {item?.extra && <p className="text-xs text-slate-500 mb-12">{item?.extra}</p>}
      <div className="flex items-center justify-between pt-16 border-t border-slate-100">
        <div className="flex items-center gap-8">
          {item?.priceRange && <><Icon name="DollarSign" size={16} className="text-slate-400" /><span className="text-sm font-semibold text-slate-900">{item?.priceRange}</span></>}
          {item?.marginPct > 0 && <span className="text-xs text-slate-500">Margin: {item?.marginPct}%</span>}
          {item?.slaCommitment && <span className="text-xs text-slate-500">SLA: {item?.slaCommitment}</span>}
        </div>
        <span className={`flex items-center gap-6 text-sm ${item?.status === 'Active' ? 'text-success' : 'text-slate-400'}`}>
          <div className={`w-2 h-2 rounded-full ${item?.status === 'Active' ? 'bg-success' : 'bg-slate-400'}`}></div>
          {item?.status}
        </span>
      </div>
    </div>
  );

  const ProductRow = ({ item }) => (
    <div className="bg-white rounded-lg border border-slate-200 p-20 hover:shadow-elevation-sm transition-smooth">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-16 flex-1">
          <div className={`p-10 rounded-lg ${item?.type === 'product' ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10' : 'bg-gradient-to-br from-purple-500/10 to-pink-500/10'}`}>
            <Icon name={item?.type === 'product' ? 'Package' : 'Wrench'} size={20} className={item?.type === 'product' ? 'text-blue-600' : 'text-purple-600'} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-12 mb-4">
              <h4 className="font-semibold text-slate-900">{item?.name}</h4>
              {item?.strategic && (
                <span className="px-10 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold rounded-full flex items-center gap-4">
                  <Icon name="Star" size={10} />Strategic
                </span>
              )}
            </div>
            {item?.description && <p className="text-sm text-slate-600">{item?.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-32">
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-2">Category</div>
            <div className="text-sm font-medium text-slate-900">{item?.category}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-2">Status</div>
            <span className={`flex items-center gap-6 text-sm ${item?.status === 'Active' ? 'text-success' : 'text-slate-400'}`}>
              <div className={`w-2 h-2 rounded-full ${item?.status === 'Active' ? 'bg-success' : 'bg-slate-400'}`}></div>
              {item?.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center gap-12">
          <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="Package" size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Products & Services</h2>
            <p className="text-sm text-slate-600">Linked from master catalog</p>
          </div>
        </div>

        <div className="flex items-center gap-8">
          {partnerId && (
            <>
              <button
                onClick={() => setShowAddProduct(true)}
                className="flex items-center gap-8 px-16 py-9 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg transition-smooth"
              >
                <Icon name="Plus" size={16} />
                Add Product
              </button>
              <button
                onClick={() => setShowAddService(true)}
                className="flex items-center gap-8 px-16 py-9 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg transition-smooth"
              >
                <Icon name="Plus" size={16} />
                Add Service
              </button>
            </>
          )}
          <button
            onClick={() => setViewMode('grid')}
            className={`p-10 rounded-lg transition-smooth ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <Icon name="Grid3x3" size={18} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-10 rounded-lg transition-smooth ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <Icon name="List" size={18} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-8 mb-24">
        {['all', 'product', 'service']?.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-20 py-10 rounded-lg font-semibold text-sm capitalize transition-smooth ${
              filter === cat
                ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary border border-primary/20' :'bg-slate-50 text-slate-600 hover:bg-slate-100'
            }`}
          >
            {cat === 'all' ? 'All Items' : `${cat}s`}
          </button>
        ))}
        <div className="ml-auto text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{filteredItems?.length}</span> items
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-48">
          <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        </div>
      ) : filteredItems?.length === 0 ? (
        <div className="text-center py-48">
          <Icon name="Package" size={40} className="text-slate-300 mx-auto mb-12" />
          <p className="text-slate-500 text-sm">No {filter === 'all' ? 'products or services' : filter + 's'} linked yet.</p>
          {partnerId && (
            <p className="text-slate-400 text-xs mt-6">Use the buttons above to add from the master catalog.</p>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
          {filteredItems?.map((item) => <ProductCard key={item?.id} item={item} />)}
        </div>
      ) : (
        <div className="space-y-12">
          {filteredItems?.map((item) => <ProductRow key={item?.id} item={item} />)}
        </div>
      )}

      {showAddProduct && (
        <AddProductModal
          partnerId={partnerId}
          linkedProductIds={linkedProductIds}
          onClose={() => setShowAddProduct(false)}
          onSuccess={() => { setShowAddProduct(false); loadItems(); }}
        />
      )}
      {showAddService && (
        <AddServiceModal
          partnerId={partnerId}
          linkedServiceIds={linkedServiceIds}
          onClose={() => setShowAddService(false)}
          onSuccess={() => { setShowAddService(false); loadItems(); }}
        />
      )}
    </div>
  );
};

export default ProductsServicesSection;